import { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, ZoomIn } from 'lucide-react';
import ScrollReveal from '@/components/react-bits/scroll-reveal';

// ============================================================
// COMMUNITY MEME DATA
// Replace imageUrl values with real community meme image URLs
// ============================================================
const COMMUNITY_MEMES = [
  {
    id: 'c1',
    imageUrl: 'https://picsum.photos/seed/badger01/800/600',
    prompt: 'Honey Badger slapping the bear market',
    author: '@claw800bot',
  },
  {
    id: 'c2',
    imageUrl: 'https://picsum.photos/seed/badger02/400/400',
    prompt: 'Honey Badger HODL through the crash',
    author: '@web3henry',
  },
  {
    id: 'c3',
    imageUrl: 'https://picsum.photos/seed/badger03/400/400',
    prompt: 'Honey Badger eating a cobra on Wall Street',
    author: '@chaosmaxi',
  },
  {
    id: 'c4',
    imageUrl: 'https://picsum.photos/seed/badger04/600/400',
    prompt: 'Honey Badger going to the moon anyway',
    author: '@moonbagger',
  },
  {
    id: 'c5',
    imageUrl: 'https://picsum.photos/seed/badger05/400/400',
    prompt: 'Honey Badger ignoring all the FUD',
    author: '@fudkilla',
  },
  {
    id: 'c6',
    imageUrl: 'https://picsum.photos/seed/badger06/400/400',
    prompt: 'Honey Badger vs SEC round 47',
    author: '@degenlord',
  },
  {
    id: 'c7',
    imageUrl: 'https://picsum.photos/seed/badger07/800/400',
    prompt: 'Honey Badger diamond hands during liquidity crunch',
    author: '@diamondbadger',
  },
  {
    id: 'c8',
    imageUrl: 'https://picsum.photos/seed/badger08/400/400',
    prompt: 'Honey Badger running the algo at 3am',
    author: '@nocturnal_ape',
  },
  {
    id: 'c9',
    imageUrl: 'https://picsum.photos/seed/badger09/400/400',
    prompt: 'Honey Badger eating cobras for breakfast',
    author: '@cobraslayer',
  },
];

// Grid sizing config — maps to Tailwind classes
const GRID_SIZES: Record<string, string> = {
  hero: 'col-span-8 row-span-1',
  square: 'col-span-4 row-span-1',
  wide: 'col-span-8 row-span-1',
  small: 'col-span-4 row-span-1',
};

const ITEM_LAYOUT = [
  { size: 'hero',   aspect: 'aspect-[16/9]' },  // 0: big top-left
  { size: 'square', aspect: 'aspect-square'  },  // 1: top-right
  { size: 'square', aspect: 'aspect-square'  },  // 2: mid-right top
  { size: 'wide',   aspect: 'aspect-[16/9]' },   // 3: mid-left wide
  { size: 'square', aspect: 'aspect-square'  },  // 4: mid-right bottom
  { size: 'small',  aspect: 'aspect-square'  },  // 5: bottom-left
  { size: 'wide',   aspect: 'aspect-[16/9]' },   // 6: bottom-center wide
  { size: 'small',  aspect: 'aspect-square'  },  // 7: bottom small
  { size: 'square', aspect: 'aspect-square'  },  // 8: bottom small
];

// ============================================================
// SPOTLIGHT HOOK — mouse-tracking radial glow
// ============================================================
function useSpotlight(containerRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const cards = el.querySelectorAll<HTMLElement>('[data-meme-card]');

    const onMove = (e: MouseEvent) => {
      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        const dist = Math.hypot(e.clientX - (rect.left + rect.width / 2), e.clientY - (rect.top + rect.height / 2));
        const intensity = Math.max(0, 1 - dist / 400);
        card.style.setProperty('--gx', `${x}%`);
        card.style.setProperty('--gy', `${y}%`);
        card.style.setProperty('--gi', `${intensity}`);
      });
    };

    const onLeave = () => {
      cards.forEach((card) => card.style.setProperty('--gi', '0'));
    };

    window.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [containerRef]);
}

// ============================================================
// MEME CARD
// ============================================================
interface MemeCardProps {
  meme: (typeof COMMUNITY_MEMES)[number];
  layout: (typeof ITEM_LAYOUT)[number];
}

function MemeCard({ meme, layout }: MemeCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      data-meme-card
      className={`${GRID_SIZES[layout.size]} ${layout.aspect} relative overflow-hidden cursor-pointer group`}
      style={{
        '--gx': '50%',
        '--gy': '50%',
        '--gi': '0',
        border: '1px solid rgba(212,175,55,0.10)',
        background: '#0a0a0a',
      } as React.CSSProperties}
      whileHover={{ scale: 1.015, zIndex: 10 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      {/* Image */}
      <img
        src={meme.imageUrl}
        alt={meme.prompt}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        style={{ opacity: hovered ? 1 : 0.72 }}
        loading="lazy"
      />

      {/* Spotlight gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          background: 'radial-gradient(circle 280px at var(--gx) var(--gy), rgba(212,175,55,0.14) 0%, transparent 70%)',
          opacity: 'var(--gi)',
        }}
      />

      {/* Border glow on hover */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300 opacity-0 group-hover:opacity-100"
        style={{
          boxShadow: 'inset 0 0 0 1px rgba(212,175,55,0.40)',
        }}
      />

      {/* Info overlay */}
      <motion.div
        className="absolute inset-0 flex flex-col justify-end p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
        }}
      >
        <p
          className="text-xs font-medium leading-snug line-clamp-2 mb-1"
          style={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.7rem' }}
        >
          {meme.prompt}
        </p>
        <div className="flex items-center justify-between">
          <span style={{ color: 'rgba(212,175,55,0.7)', fontSize: '0.65rem' }}>
            {meme.author}
          </span>
          <div className="flex gap-2">
            <a
              href={meme.imageUrl}
              download
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="transition-colors"
              style={{ color: 'rgba(212,175,55,0.6)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = '#D4AF37'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(212,175,55,0.6)'; }}
            >
              <Download size={12} />
            </a>
            <ZoomIn size={12} style={{ color: 'rgba(212,175,55,0.6)' }} />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function MemeGallery() {
  const containerRef = useRef<HTMLDivElement>(null);
  useSpotlight(containerRef);

  const memes = COMMUNITY_MEMES.slice(0, ITEM_LAYOUT.length);

  return (
    <section id="gallery" className="relative section overflow-hidden">
      {/* Top divider */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          width: 'min(680px, 82vw)',
          height: 8,
          background: 'linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.55) 50%, transparent 100%)',
          clipPath: 'polygon(18px 0%, calc(100% - 18px) 0%, 100% 100%, 0% 100%)',
        }}
      />

      <div className="section-content relative z-10">
        {/* Header */}
        <ScrollReveal>
          <p className="text-label text-center mb-4" style={{ color: '#D4AF37' }}>◆ &nbsp; Community Gallery &nbsp; ◆</p>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <h2 className="text-headline font-black text-center mb-4">
            <span style={{ color: '#FFFFFF' }}>Made by </span>
            <span className="text-gold">the Hive</span>
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={0.2}>
          <p className="text-body text-center mb-12" style={{ color: 'rgba(255,255,255,0.38)' }}>
            Chaos generated by our community. Add yours with the meme lab above.
          </p>
        </ScrollReveal>

        {/* Bento Grid */}
        <ScrollReveal delay={0.25}>
          <div
            ref={containerRef}
            className="grid grid-cols-12 gap-2 w-full max-w-6xl mx-auto"
          >
            {memes.map((meme, i) => (
              <MemeCard key={meme.id} meme={meme} layout={ITEM_LAYOUT[i]} />
            ))}
          </div>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal delay={0.3}>
          <div className="mt-10 text-center">
            <p className="text-caption mb-4" style={{ color: 'rgba(255,255,255,0.24)' }}>
              Generate your own and join the chaos
            </p>
            <a
              href="#memes"
              className="inline-flex items-center gap-2 px-6 py-3 text-caption font-semibold transition-all duration-200"
              style={{
                border: '1px solid rgba(212,175,55,0.35)',
                color: '#D4AF37',
                background: 'rgba(212,175,55,0.06)',
                clipPath: 'polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(212,175,55,0.15)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(212,175,55,0.06)'; }}
            >
              Open Meme Lab
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
